import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { StreetNameService } from "../services/StreetNameService";

export const arrondissements = [
  { name: "Ahuntsic-Cartierville", id: "da06242e-86c7-4e97-baf2-4a13dc33ebc0" },
  { name: "Anjou", id: "5b405217-6ebc-45e9-bf73-9269dd9666f6" },
  { name: "Côte-des-Neiges—Notre-Dame-de-Grâce", id: "8819693b-870a-4288-bb09-f7eb20a6f095" },
  { name: "Île-Bizard–Sainte-Geneviève", id: "36fd87af-6c92-45d0-9de1-8204fc1d7d95" },
  { name: "Lachine", id: "a3e11c74-9502-4ad5-a95f-43a0fa8e5c04" },
  { name: "LaSalle", id: "d17b9159-d9ea-4431-88f4-1fb9735cd084" },
  { name: "Le Plateau-Mont-Royal", id: "13ad610a-5312-4765-aedd-8d016261480f" },
  { name: "Le Sud-Ouest", id: "6230ea2f-2d84-4e3f-80bd-6bf56bb80b35" },
  { name: "Mercier–Hochelaga-Maisonneuve", id: "1f74c2e0-a55a-46f2-923a-dbf91ee6bd1a" },
  { name: "Montréal-Nord", id: "6f45a3c9-0354-4f3b-89e6-099715ee1ac8" },
  { name: "Outremont", id: "07961002-d29c-43e3-a1ec-9966edc2e0e3" },
  { name: "Pierrefonds-Roxboro", id: "06533106-4ab7-474e-bb2a-b37837917782" },
  { name: "Rivière-des-Prairies–Pointe-aux-Trembles", id: "6266baa4-4392-4222-a96b-aa91981b351c" },
  { name: "Rosemont–La Petite-Patrie", id: "d2affd10-0879-490d-b296-f9fc0d225bfa" },
  { name: "Saint-Laurent", id: "544e7fbb-46af-46af-974f-d47d57557aed" },
  { name: "Saint-Léonard", id: "e58a361b-9fbe-4b13-a471-8efd9172e563" },
  { name: "Verdun", id: "cecf74d7-902e-42bc-93e8-4dfa687a8b64" },
  { name: "Ville-Marie", id: "31d21f1c-b084-4bdd-b65f-da94e3cf6417" },
  { name: "Villeray–Saint-Michel–Parc-Extension", id: "9bfc305c-e930-4dfb-a345-6355bae4cadd" },
];

export const AddressFields = ({ 
  streetNumber, setStreetNumber,
  streetName, setStreetName,
  appNumber, setAppNumber,
  arrondissement, setArrondissement,
  arrondissementError
}) => {
  const { t } = useTranslation();
  const [streetSuggestions, setStreetSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const normalizeCivicNumber = (value) => String(value || '').replace(/\D+/g, '');

  // Get the arrondissement name for filtering
  const selectedArrondissementName = arrondissements.find(arr => arr.id === arrondissement)?.name || null;

  useEffect(() => {
    if (streetName.length >= 2) {
      const suggestions = StreetNameService.filterStreetNames(streetName, 2, 10, selectedArrondissementName);
      setStreetSuggestions(suggestions);
    } else {
      setStreetSuggestions([]);
    }
  }, [streetName, selectedArrondissementName]);

  const handleStreetKeyDown = (e) => {
    if (e.key === "Enter" && streetSuggestions.length > 0) {
      e.preventDefault();
      setStreetName(streetSuggestions[0]);
      setShowSuggestions(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("2026.address.borough", "Arrondissement")}
        </label>
        <select
          value={arrondissement}
          onChange={(e) => setArrondissement(e.target.value)}
          className={`w-full p-2 border rounded focus:ring-emerald-500 focus:border-emerald-500 ${arrondissementError ? 'border-red-500' : ''}`}
          required
        >
          <option value="">{t("2026.address.selectBorough", "Choisir un arrondissement")}</option>
          {arrondissements.map((arr) => (
            <option key={arr.id} value={arr.id}>
              {arr.name}
            </option>
          ))}
        </select>
        {arrondissementError && (
          <p className="text-sm text-red-500 mt-1">{arrondissementError}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("2026.address.streetNumber", "Numéro civique")}
          </label>
          <input
            id="civic-number-input"
            name="civicNumber"
            type="text"
            value={streetNumber}
            onChange={(e) => setStreetNumber(normalizeCivicNumber(e.target.value))}
            className="w-full p-2 border rounded focus:ring-emerald-500 focus:border-emerald-500"
            placeholder="1234"
            inputMode="numeric"
            autoComplete="new-password"
            data-lpignore="true"
          />
          <p className="text-xs text-gray-500 mt-1">
            {t("2026.address.streetNumberHint", "Chiffres seulement (ex.: 150A → 150)")}
          </p>
        </div>

        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("2026.address.streetName", "Nom de rue")}
          </label>
          <div className="relative">
            <input
              id="road-name-input"
              name="roadName"
              type="text"
              value={streetName}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                setStreetName(value);
                if (value.length >= 2) {
                  setShowSuggestions(true);
                } else {
                  setShowSuggestions(false);
                }
              }}
              onFocus={() => streetName.length >= 2 && setShowSuggestions(true)}
              onBlur={() => {
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              onKeyDown={handleStreetKeyDown}
              className="w-full p-2 border rounded focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="DE L'ÉGLISE"
              autoComplete="new-password"
              data-lpignore="true"
            />
            {showSuggestions && streetSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm border">
                {streetSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                    onClick={() => {
                      setStreetName(suggestion);
                      setShowSuggestions(false);
                    }}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("2026.address.appNumber", "Appartement (optionnel)")}
          </label>
          <input
            type="text"
            name="unitNumber"
            value={appNumber}
            onChange={(e) => setAppNumber(e.target.value)}
            className="w-full p-2 border rounded focus:ring-emerald-500 focus:border-emerald-500"
            placeholder="102"
            inputMode="numeric"
            autoComplete="new-password"
            data-lpignore="true"
          />
        </div>
      </div>
    </div>
  );
};
